// ToolFleetPage.tsx — Shared tools → Adoption (`/shared-tools/adoption`).
//
// Renders the per-tool adoption matrix from `GET /fleet/tool-adoption` as a
// filterable, sortable table: for each jankurai tool, how many repos have
// adopted it and how many are applicable-but-missing. Each tool links to its
// detail page (`/shared-tools/adoption/:tool`). Data is projected from each repo's latest
// recorded score — no extra computation.
//
// Each entry names the repos adopting the tool and the repos that should: with
// a family in scope both lists are cut to that family's repos, so "3/7" is
// this family's adoption rather than the fleet's. `GET /fleet/tool-adoption`
// takes no family, so the cut is made here from the repo→family map the shell
// already has, and a tool with no repo left in scope drops out of the table.

import { ArrowDown, ArrowUp, Boxes } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { pillClass, type Tone } from '../components/tone/tone';
import { ErrorState, LoadingState } from '../components/state';
import { ScopedEmptyState } from '../components/family/ScopedEmptyState';
import { OtherFamilies } from '../components/family/OtherFamilies';
import { useRepoFamilyScope } from '../components/family/useRepoFamilyScope';
import { useToolFleet } from '../hooks/useToolFleet';
import {
  defaultDirection,
  scopeToolAdoption,
  DEFAULT_TOOL_FLEET_FILTERS,
  DEFAULT_TOOL_FLEET_SORT,
  projectToolFleet,
  toolCategories,
  toolFleetFiltersFrom,
  toolFleetSortFrom,
  type AdoptionStatus,
  type ToolFleetFilters,
  type ToolFleetRow,
  type ToolFleetSort,
  type ToolFleetSortKey,
} from './toolFleetModel';
import './page.css';
import './ToolFleetPage.css';
import { PageHeader, queryFreshness } from '../components/pageHeader';
import { ADOPTION_PATH, SharedToolsTabs } from './sharedTools/SharedToolsTabs';
import { usePageTitle } from '../hooks/usePageTitle';
import { useViewState } from '../hooks/useViewState';

const COLUMNS: { key: ToolFleetSortKey; label: string; numeric?: boolean }[] = [
  { key: 'tool', label: 'Tool' },
  { key: 'category', label: 'Category' },
  { key: 'adoption', label: 'Adoption', numeric: true },
  { key: 'adopted', label: 'Adopting', numeric: true },
  { key: 'missing', label: 'Should adopt', numeric: true },
];

const STATUS_TONE: Record<AdoptionStatus, Tone> = {
  complete: 'ok',
  partial: 'warn',
  none: 'failed',
};

export function adoptionPillClass(row: ToolFleetRow): string {
  return pillClass(row.total === 0 ? 'unknown' : STATUS_TONE[row.status]);
}

function SortHeader({
  column,
  sort,
  onSort,
}: {
  column: (typeof COLUMNS)[number];
  sort: ToolFleetSort;
  onSort: (key: ToolFleetSortKey) => void;
}): JSX.Element {
  const active = sort.key === column.key;
  const Arrow = sort.direction === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      className={column.numeric ? 'tool-fleet__num' : undefined}
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="tool-fleet__sort"
        data-testid={`tool-fleet-sort-${column.key}`}
        onClick={() => onSort(column.key)}
      >
        {column.label}
        {active ? <Arrow size={12} aria-hidden="true" /> : null}
      </button>
    </th>
  );
}

function ToolRow({ row }: { row: ToolFleetRow }): JSX.Element {
  const { entry } = row;
  return (
    <tr data-testid={`tool-row-${entry.tool}`}>
      <td>
        <Link className="tool-fleet__tool" to={`${ADOPTION_PATH}/${encodeURIComponent(entry.tool)}`}>
          {entry.tool}
        </Link>
      </td>
      <td>
        <span className="page__pill">{entry.category}</span>
      </td>
      <td className="tool-fleet__num">
        <span className={adoptionPillClass(row)}>
          {row.adopted}/{row.total}
        </span>
        <span className="tool-fleet__bar" aria-hidden="true">
          <span style={{ width: `${Math.round(row.ratio * 100)}%` }} />
        </span>
      </td>
      <td className="tool-fleet__num">{row.adopted}</td>
      <td className="tool-fleet__num">{row.missing}</td>
    </tr>
  );
}

export function ToolFleetPage(): JSX.Element {
  usePageTitle('Tool adoption');
  const fleet = useToolFleet();
  const { data, isPending, isError, error } = fleet;
  // The filters and the sort are in the URL, so a filtered, sorted table is a
  // link. Both narrow one view, so both replace: Back leaves the page.
  const view = useViewState();
  const filters = useMemo(() => toolFleetFiltersFrom((param) => view.read(param)), [view]);
  const sort = useMemo(() => toolFleetSortFrom((param) => view.read(param)), [view]);
  const setFilters = (next: ToolFleetFilters): void =>
    view.write(
      {
        q: next.search,
        category: next.category === DEFAULT_TOOL_FLEET_FILTERS.category ? null : next.category,
        status: next.status === DEFAULT_TOOL_FLEET_FILTERS.status ? null : next.status,
      },
      'replace'
    );

  const scope = useRepoFamilyScope();
  const scoped = useMemo(
    () => scopeToolAdoption(data?.tools ?? [], (repo) => scope.keeps(repo), scope.active),
    [data?.tools, scope]
  );
  const tools = scoped.rows;
  const categories = useMemo(() => toolCategories(tools), [tools]);
  const rows = useMemo(
    () => projectToolFleet(tools, filters, sort),
    [tools, filters, sort]
  );

  const onSort = (key: ToolFleetSortKey): void => {
    const direction =
      sort.key === key
        ? sort.direction === 'asc'
          ? 'desc'
          : 'asc'
        : defaultDirection(key);
    const fallback =
      key === DEFAULT_TOOL_FLEET_SORT.key && direction === DEFAULT_TOOL_FLEET_SORT.direction;
    view.write({ sort: fallback ? null : key, dir: fallback ? null : direction }, 'replace');
  };

  return (
    <div className="page page--wide" data-testid="tool-fleet-page">
      <PageHeader
        title="Shared tools"
        testId="shared-tools-header"
        freshness={queryFreshness(fleet)}
        subtitle={
          <>
          Where each shared tool is in use: which repos have adopted it, and
          which should but haven&apos;t yet. Read from each repo&apos;s latest
          jankurai score, recorded on every push.
          </>
        }
      >
      <SharedToolsTabs />
      </PageHeader>

      {isPending ? (
        <LoadingState title="Loading tool adoption…" variant="message" />
      ) : isError ? (
        <ErrorState title="Could not load tool adoption." error={error} />
      ) : tools.length === 0 ? (
        <>
          <ScopedEmptyState
            icon={Boxes}
            title="No tool-adoption data yet."
            description="Once repos are scored, their tool adoption appears here."
          />
          <OtherFamilies count={scoped.elsewhere} noun="tools" />
        </>
      ) : (
        <section className="page__section">
          <div className="tool-fleet__toolbar" role="search">
            <input
              type="search"
              className="tool-fleet__input"
              placeholder="Filter by tool, category or repo"
              aria-label="Filter tools"
              data-testid="tool-fleet-search"
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            />
            <select
              className="tool-fleet__input"
              aria-label="Category"
              data-testid="tool-fleet-category"
              value={filters.category}
              onChange={(event) => setFilters({ ...filters, category: event.target.value })}
            >
              <option value="all">All categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            <select
              className="tool-fleet__input"
              aria-label="Adoption"
              data-testid="tool-fleet-status"
              value={filters.status}
              onChange={(event) =>
                setFilters({
                  ...filters,
                  status: event.target.value as ToolFleetFilters['status'],
                })
              }
            >
              <option value="all">Any adoption</option>
              <option value="complete">Fully adopted</option>
              <option value="partial">Partially adopted</option>
              <option value="none">Not adopted</option>
            </select>
            <span className="tool-fleet__count">
              {rows.length} of {tools.length} tools ·{' '}
              {scope.active ? scope.label : `${data.repos_scored} repos scored`}
            </span>
          </div>

          <div className="table-scroll">
            <table className="tool-fleet__table" data-testid="tool-fleet-table">
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <SortHeader key={column.key} column={column} sort={sort} onSort={onSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ToolRow key={row.entry.tool} row={row} />
                ))}
              </tbody>
            </table>
          </div>
          {rows.length === 0 ? (
            <p className="tool-fleet__none">No tools match these filters.</p>
          ) : null}
          <OtherFamilies count={scoped.elsewhere} noun="tools" />
        </section>
      )}
    </div>
  );
}
